import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isBOD: boolean;
  isSales: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBOD, setIsBOD] = useState(false);
  const [isSales, setIsSales] = useState(false);

  const fetchUserFlags = async (authUserId: string) => {
    try {
      const { data } = await supabase
        .from('users')
        .select('is_bod, is_sales')
        .or(`auth_user_id.eq.${authUserId},user_id.eq.${authUserId}`)
        .limit(1)
        .maybeSingle();
      setIsBOD(data?.is_bod ?? false);
      setIsSales((data as { is_sales?: boolean } | null)?.is_sales ?? false);
    } catch {
      setIsBOD(false);
      setIsSales(false);
    }
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const initializeAuth = async () => {
      try {
        timeoutId = setTimeout(() => {
          setLoading(false);
        }, 5000);

        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('❌ AuthProvider: Error getting session:', error);
        }

        clearTimeout(timeoutId);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Fetch BOD status in background — không block UI
        if (session?.user) {
          fetchUserFlags(session.user.id);
        }
      } catch (error) {
        console.error('❌ AuthProvider: Unexpected error:', error);
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Fetch BOD status in background — không block UI
      if (session?.user) {
        fetchUserFlags(session.user.id);
      } else {
        setIsBOD(false);
        setIsSales(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []);

  const signInWithGoogle = async () => {
    // For Supabase OAuth, we should use the Supabase callback URL
    // The redirectTo should be where users go AFTER successful authentication
    const isProduction = window.location.hostname !== 'localhost';
    const redirectUrl = isProduction 
      ? 'https://1report-ecotech.vercel.app/'  // Redirect to home page after auth
      : 'http://localhost:8081/';              // Redirect to home page after auth
    
    console.log('Authentication redirect URL:', redirectUrl);
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // Chi xin scope non-sensitive. drive.readonly la restricted scope: no ep
        // OAuth app phai la Internal (chan tai khoan ngoai eco-tech.vn) hoac phai
        // qua Google verification. Khong code nao dung session.provider_token nua
        // - file Drive duoc doc bang service account qua edge function
        // download-from-drive - nen scope nay la du thua.
        scopes: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
        redirectTo: redirectUrl,  // This is where user goes AFTER auth, not the OAuth callback
        queryParams: {
          // Bat buoc Google hien man hinh chon tai khoan. Neu khong co tham so nay,
          // khi trinh duyet chi dang dang nhap 1 tai khoan Google thi Google tu dong
          // chon luon tai khoan do va bat nguoc ve app - nguoi dung khong doi duoc
          // tai khoan sau khi bam Dang xuat. supabase.auth.signOut() chi xoa phien
          // Supabase, no khong (va khong nen) dang xuat khoi Google.
          prompt: 'select_account',
        },
      },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const value = {
    user,
    session,
    loading,
    isBOD,
    isSales,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
